import { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useRoutes } from 'react-router-dom';
import { ToastProvider } from '../../notifications/ToastProvider';
import { NotificationHubProvider } from '../../realtime/NotificationHubProvider';
import { shellRouteObjects } from '../../routes/shellRouteObjects';
import { testIds } from '../../testing/testIds';
import { GlobalReportSettingsBar } from './GlobalReportSettingsBar';
import { Header } from './Header';
import { PINNED_NAVIGATION_MIN_WIDTH, shouldPinNavigation } from './navigationLayout';
import { Sidebar } from './Sidebar';

const NAVIGATION_PINNED_STORAGE_KEY = 'ledgervue-navigation-pinned';

function initialPinnedPreference(): boolean {
  try {
    return window.localStorage.getItem(NAVIGATION_PINNED_STORAGE_KEY) !== 'false';
  } catch {
    return true;
  }
}

function useShellWidth() {
  const shellRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(() => window.innerWidth);

  useLayoutEffect(() => {
    const element = shellRef.current;
    if (!element) return;

    const update = () => setWidth(element.getBoundingClientRect().width || window.innerWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { shellRef, width };
}

function RouteLoadingFallback() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      <span className="ml-3 text-gray-600">Loading screen...</span>
    </div>
  );
}

export function Shell() {
  const routeElement = useRoutes(shellRouteObjects);
  const { shellRef, width: shellWidth } = useShellWidth();
  const contentRef = useRef<HTMLDivElement>(null);
  const navigationMenuRef = useRef<HTMLButtonElement>(null);
  const [pinnedPreference, setPinnedPreference] = useState(initialPinnedPreference);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pinned = shouldPinNavigation(shellWidth, pinnedPreference);
  const canPin = shellWidth >= PINNED_NAVIGATION_MIN_WIDTH;

  useEffect(() => {
    try {
      window.localStorage.setItem(NAVIGATION_PINNED_STORAGE_KEY, String(pinnedPreference));
    } catch {
      // Browsers can deny storage in private or embedded contexts; navigation still works for this session.
    }
  }, [pinnedPreference]);

  useEffect(() => {
    if (pinned) setDrawerOpen(false);
  }, [pinned]);

  useEffect(() => {
    if (!drawerOpen || pinned) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [drawerOpen, pinned]);

  useEffect(() => {
    // Keep keyboard focus in the active drawer rather than allowing a tab into
    // controls visually covered by its backdrop. See Sidebar for the matching
    // inert handling while the drawer itself is closed.
    contentRef.current?.toggleAttribute('inert', !pinned && drawerOpen);
  }, [drawerOpen, pinned]);

  const togglePinnedPreference = () => {
    setPinnedPreference((current) => !current);
  };

  const toggleNavigation = () => {
    if (pinned) {
      setPinnedPreference(false);
      setDrawerOpen(true);
      return;
    }
    setDrawerOpen((current) => !current);
  };

  return (
    <ToastProvider>
      <NotificationHubProvider>
        <div ref={shellRef} className="flex h-screen h-dvh bg-gray-100" data-testid={testIds.shell.root}>
          {!pinned && drawerOpen ? (
            <button
              type="button"
              className="fixed inset-0 z-40 cursor-default bg-black/30"
              aria-label="Close navigation"
              tabIndex={-1}
              data-testid={testIds.shell.navigationBackdrop}
              onClick={() => setDrawerOpen(false)}
            />
          ) : null}
          <Sidebar
            pinned={pinned}
            open={pinned || drawerOpen}
            canPin={canPin}
            onTogglePin={togglePinnedPreference}
            onClose={() => setDrawerOpen(false)}
            onNavigate={() => {
              if (!pinned) setDrawerOpen(false);
            }}
          />
          <div ref={contentRef} className="flex-1 flex flex-col overflow-hidden">
            <Header
              navigationPinned={pinned}
              navigationOpen={!pinned && drawerOpen}
              onToggleNavigation={toggleNavigation}
              navigationMenuRef={navigationMenuRef}
            />
            <GlobalReportSettingsBar />
            <main className="flex-1 overflow-auto p-4 sm:p-6">
              <Suspense fallback={<RouteLoadingFallback />}>{routeElement}</Suspense>
            </main>
          </div>
        </div>
      </NotificationHubProvider>
    </ToastProvider>
  );
}
